import { useMemo, useRef, useState, type DragEvent, type ReactNode, type RefObject } from "react";

import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { HubTableScrollRegion } from "@/components/layout/HubWorkspace";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { HoverHintContent } from "@/components/ui/title-help";
import type { HubViewportChrome } from "@/lib/hubLayoutClassNames";
import { hubStickyTableHeadClassName } from "@/lib/hubLayoutClassNames";
import { reorderColumnIds, toggleColumnHidden } from "@/lib/hubDataTableColumnLayout";
import { useHubDataTableColumnLayout } from "@/lib/useHubDataTableColumnLayout";
import { cn } from "@/lib/utils";

import { HubDataTableColumnLayoutMenu } from "./HubDataTableColumnLayoutMenu";

export type HubDataTextOverflow = "wrap" | "nowrap" | "truncate";

export type HubDataTableColumn<Row, SortKey extends string = string> = {
  id: string;
  header: ReactNode;
  /** Defaults to `id`. Every column is sortable. */
  sortKey?: SortKey;
  /** Optional sort value when `cell` returns a React node. Defaults to string/number cell output. */
  sortValue?: (row: Row) => string | number | null | undefined;
  align?: "left" | "right";
  /**
   * Default `wrap`: long text wraps inside the card (no ellipsis).
   * Use `nowrap` for short ids/phones; `truncate` only when you explicitly want ellipsis + hover.
   */
  overflow?: HubDataTextOverflow;
  headerClassName?: string;
  cellClassName?: string;
  cell: (row: Row) => ReactNode;
};

export type HubDataTableProps<Row, SortKey extends string = string> = {
  columns: readonly HubDataTableColumn<Row, SortKey>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string | number;
  chrome?: HubViewportChrome;
  title?: ReactNode;
  headerExtra?: ReactNode;
  /** Controlled sort key. When omitted with `onSort`, the table sorts internally. */
  sortKey?: SortKey;
  sortDir?: SortDirection;
  onSort?: (key: SortKey) => void;
  loading?: boolean;
  loadingMessage?: ReactNode;
  error?: ReactNode;
  empty?: ReactNode;
  isFiltered?: boolean;
  filteredEmpty?: ReactNode;
  className?: string;
  tableClassName?: string;
  onRowClick?: (row: Row) => void;
  rowClassName?: (row: Row) => string | undefined;
  /** When false, omit the viewport max-height shell (compact demos / nested cards). Default true. */
  fillViewport?: boolean;
  /** Initial sort when sort is not controlled via `sortKey` / `onSort`. */
  defaultSortKey?: SortKey;
  defaultSortDir?: SortDirection;
  /**
   * Freeze the displayed row order after the first load or a header sort. Inline edits stay
   * put until the operator sorts again, refreshes, or leaves. New rows append in sort order.
   */
  keepRowOrderUntilResort?: boolean;
  /** Forwarded to the inner scroll region (infinite scroll roots, and similar). */
  scrollRegionRef?: RefObject<HTMLDivElement | null>;
  /** Rendered inside the scroll region below the table (sentinels, load-more hints). */
  listScrollFooter?: ReactNode;
  /**
   * When set, enables drag-to-reorder headers, a column visibility menu, and per-user layout
   * persistence in `workflow.hub_user_ui_state` via `/api/me/ui-state`.
   */
  columnLayoutTableId?: string;
};

function cellOverflowClass(overflow: HubDataTextOverflow): string {
  if (overflow === "truncate") return "min-w-0 max-w-[18rem]";
  if (overflow === "nowrap") return "whitespace-nowrap";
  return "min-w-0 break-words";
}

function compareSortValues(left: string | number | null | undefined, right: string | number | null | undefined): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true });
}

function resolveColumnSortKey<Row, SortKey extends string>(
  column: HubDataTableColumn<Row, SortKey>,
): SortKey {
  return (column.sortKey ?? column.id) as SortKey;
}

function resolveSortValue<Row, SortKey extends string>(
  column: HubDataTableColumn<Row, SortKey>,
  row: Row,
): string | number | null | undefined {
  if (column.sortValue != null) return column.sortValue(row);
  const content = column.cell(row);
  if (typeof content === "string" || typeof content === "number") return content;
  return "";
}

/** Text cell. Default wraps; pass `overflow="truncate"` for ellipsis + hover. */
export function HubDataTextCell({
  value,
  overflow = "wrap",
  className,
}: {
  value: string;
  overflow?: HubDataTextOverflow;
  className?: string;
}): JSX.Element {
  const text = value.trim() === "" ? "—" : value;
  if (overflow === "truncate" && text !== "—") {
    return (
      <HoverHintContent hoverHint={text} block cursor="default" className={className}>
        <span className="block truncate">{text}</span>
      </HoverHintContent>
    );
  }
  return <span className={cn(cellOverflowClass(overflow), className)}>{text}</span>;
}

/**
 * Normal hub data table: viewport card, clipped overflow, sticky header,
 * always-sortable columns, wrap-by-default text, and custom cells for buttons or editors.
 */
export function HubDataTable<Row, SortKey extends string = string>({
  columns,
  rows,
  rowKey,
  chrome = "standard",
  title,
  headerExtra,
  sortKey: sortKeyProp,
  sortDir: sortDirProp,
  onSort: onSortProp,
  loading = false,
  loadingMessage = "Loading…",
  error = null,
  empty = "No rows.",
  isFiltered = false,
  filteredEmpty = "No rows match these filters.",
  className,
  tableClassName,
  onRowClick,
  rowClassName,
  fillViewport = true,
  defaultSortKey,
  defaultSortDir,
  keepRowOrderUntilResort = false,
  scrollRegionRef,
  listScrollFooter,
  columnLayoutTableId,
}: HubDataTableProps<Row, SortKey>): JSX.Element {
  const layoutTableId = columnLayoutTableId?.trim() ?? "";
  const layoutEnabled = layoutTableId !== "";

  const allColumnIds = useMemo(() => columns.map((column) => column.id), [columns]);

  const { columnOrder, hiddenColumnIds, setColumnOrder, setHiddenColumnIds, resetLayout } =
    useHubDataTableColumnLayout(layoutTableId, allColumnIds, layoutEnabled);

  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);

  const hiddenColumnIdSet = useMemo(() => new Set(hiddenColumnIds), [hiddenColumnIds]);

  const displayColumns = useMemo(() => {
    if (!layoutEnabled) {
      return columns;
    }
    const byId = new Map(columns.map((column) => [column.id, column]));
    return columnOrder
      .filter((id) => !hiddenColumnIdSet.has(id))
      .map((id) => byId.get(id))
      .filter((column): column is HubDataTableColumn<Row, SortKey> => column != null);
  }, [columnOrder, columns, hiddenColumnIdSet, layoutEnabled]);

  const layoutColumnMenu =
    layoutEnabled && allColumnIds.length > 0
      ? (
          <HubDataTableColumnLayoutMenu
            columnIds={columnOrder}
            hiddenColumnIds={hiddenColumnIdSet}
            onToggleColumn={(columnId, visible) => {
              setHiddenColumnIds((current) => toggleColumnHidden(columnOrder, current, columnId, visible));
            }}
            onResetLayout={resetLayout}
          />
        )
      : null;

  const mergedHeaderExtra =
    layoutColumnMenu != null
      ? (
          <div className="flex flex-wrap items-center gap-2">
            {layoutColumnMenu}
            {headerExtra}
          </div>
        )
      : headerExtra;

  const firstSortKey = resolveColumnSortKey(displayColumns[0] ?? columns[0] ?? { id: "id", header: "", cell: () => "" });
  const [internalSortKey, setInternalSortKey] = useState<SortKey>(sortKeyProp ?? defaultSortKey ?? firstSortKey);
  const [internalSortDir, setInternalSortDir] = useState<SortDirection>(
    sortDirProp ?? defaultSortDir ?? "asc",
  );

  const controlled = onSortProp != null && sortKeyProp != null;
  const activeSortKey = controlled ? sortKeyProp! : internalSortKey;
  const activeSortDir = controlled ? (sortDirProp ?? "asc") : internalSortDir;

  function handleSort(key: SortKey) {
    if (controlled) {
      onSortProp!(key);
      return;
    }
    if (key === internalSortKey) {
      setInternalSortDir((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setInternalSortKey(key);
      setInternalSortDir("asc");
    }
  }

  const rowOrderRef = useRef<{ epoch: string; ids: string[] }>({ epoch: "", ids: [] });
  const sortEpoch = `${String(activeSortKey)}:${activeSortDir}`;
  if (keepRowOrderUntilResort && !controlled && rowOrderRef.current.epoch !== sortEpoch) {
    rowOrderRef.current = { epoch: sortEpoch, ids: [] };
  }

  const displayRows = useMemo(() => {
    if (controlled) return rows;
    const column =
      displayColumns.find((item) => resolveColumnSortKey(item) === activeSortKey) ?? displayColumns[0];
    if (column == null) return rows;
    const compareRows = (left: Row, right: Row) => {
      const result = compareSortValues(resolveSortValue(column, left), resolveSortValue(column, right));
      return activeSortDir === "asc" ? result : -result;
    };
    if (!keepRowOrderUntilResort) {
      return [...rows].sort(compareRows);
    }
    if (rowOrderRef.current.ids.length === 0) {
      const sorted = [...rows].sort(compareRows);
      rowOrderRef.current.ids = sorted.map((row) => String(rowKey(row)));
      return sorted;
    }
    const byId = new Map(rows.map((row) => [String(rowKey(row)), row]));
    const seen = new Set<string>();
    const kept: Row[] = [];
    for (const id of rowOrderRef.current.ids) {
      const row = byId.get(id);
      if (row == null) continue;
      kept.push(row);
      seen.add(id);
    }
    const fresh = rows.filter((row) => !seen.has(String(rowKey(row)))).sort(compareRows);
    rowOrderRef.current.ids = [...kept.map((row) => String(rowKey(row))), ...fresh.map((row) => String(rowKey(row)))];
    return [...kept, ...fresh];
  }, [activeSortDir, activeSortKey, controlled, displayColumns, keepRowOrderUntilResort, rowKey, rows]);

  const emptyMessage = isFiltered ? filteredEmpty : empty;

  const shell = (
    <>
      {title != null || mergedHeaderExtra != null ? (
        <CardHeader className="flex shrink-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          {title != null ? <CardTitle>{title}</CardTitle> : <span />}
          {mergedHeaderExtra}
        </CardHeader>
      ) : null}
      <CardBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
        {loading ? (
          <p className="px-4 py-6 text-sm text-slate-600">{loadingMessage}</p>
        ) : error != null ? (
          <div className="px-4 py-6 text-sm text-red-700">{error}</div>
        ) : (
          <HubTableScrollRegion ref={scrollRegionRef}>
            <table
              className={cn(
                "w-full border-separate border-spacing-0 text-left text-sm",
                tableClassName,
              )}
            >
              <thead className={hubStickyTableHeadClassName("slate")}>
                <tr>
                  {displayColumns.map((column) => {
                    const columnSortKey = resolveColumnSortKey(column);
                    const reorder =
                      layoutEnabled
                        ? {
                            isDragOver: dragOverColumnId === column.id,
                            onDragStart: (event: DragEvent<HTMLButtonElement>) => {
                              setDraggedColumnId(column.id);
                              event.dataTransfer.effectAllowed = "move";
                              event.dataTransfer.setData("text/plain", column.id);
                            },
                            onDragOver: (event: DragEvent<HTMLTableCellElement>) => {
                              event.preventDefault();
                              event.dataTransfer.dropEffect = "move";
                              setDragOverColumnId(column.id);
                            },
                            onDrop: (event: DragEvent<HTMLTableCellElement>) => {
                              event.preventDefault();
                              const sourceId = draggedColumnId ?? event.dataTransfer.getData("text/plain");
                              if (sourceId.trim() !== "") {
                                setColumnOrder((current) => reorderColumnIds(current, sourceId, column.id));
                              }
                              setDragOverColumnId(null);
                              setDraggedColumnId(null);
                            },
                            onDragEnd: () => {
                              setDragOverColumnId(null);
                              setDraggedColumnId(null);
                            },
                          }
                        : undefined;
                    return (
                      <SortableTableHeader
                        key={column.id}
                        label={column.header}
                        sortKey={columnSortKey}
                        activeKey={activeSortKey}
                        dir={activeSortDir}
                        onSort={handleSort}
                        align={column.align ?? "left"}
                        className={cn("sticky top-0 z-20", column.headerClassName)}
                        columnReorder={reorder}
                      />
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(displayColumns.length, 1)} className="px-3 py-6 text-slate-600">
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row) => (
                    <tr
                      key={rowKey(row)}
                      className={cn(
                        "hover:bg-slate-50/80",
                        onRowClick != null && "cursor-pointer",
                        rowClassName?.(row),
                      )}
                      onClick={onRowClick != null ? () => onRowClick(row) : undefined}
                    >
                      {displayColumns.map((column) => {
                        const overflow = column.overflow ?? "wrap";
                        const content = column.cell(row);
                        const asText = typeof content === "string" || typeof content === "number";
                        return (
                          <td
                            key={column.id}
                            className={cn(
                              "border-b border-slate-100 px-2 py-2 align-top",
                              column.align === "right" && "text-right tabular-nums",
                              asText ? cellOverflowClass(overflow) : "min-w-0",
                              column.cellClassName,
                            )}
                          >
                            {asText ? (
                              <HubDataTextCell value={String(content)} overflow={overflow} />
                            ) : (
                              content
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {listScrollFooter}
          </HubTableScrollRegion>
        )}
      </CardBody>
    </>
  );

  if (!fillViewport) {
    return (
      <Card className={cn("flex max-h-[22rem] min-h-0 flex-col overflow-hidden", className)}>{shell}</Card>
    );
  }

  return (
    <HubViewportCard chrome={chrome} className={cn("overflow-hidden", className)}>
      {shell}
    </HubViewportCard>
  );
}
